import { AddressBook, Check, FunnelSimple, GraduationCap } from '@phosphor-icons/react';
import type { FormationDuServeur, MembreDeLAnnuaire } from '@sirocco/shared';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { SearchField } from '../../components/ui/Field';
import { Popover } from '../../components/ui/Popover';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { t } from '../../i18n';
import {
  filtreActif,
  filtrer,
  FiltresDeLAnnuaire,
  ListeParLettre,
  SANS_FILTRE,
  SANS_FORMATION,
  useAnnuaire,
  useToast,
} from './commun';

/**
 * Les etudiants d'une ecole : tous ses membres, pour retrouver quelqu'un
 * et lui ecrire. On y arrive par l'accueil de l'annuaire, a cote des
 * associations.
 *
 * Les suggestions d'amis tournent dans un vivier de vingt : bien pour
 * rencontrer, inutilisable pour chercher la personne croisee en cours ce
 * matin. Ici tout le monde est la.
 *
 * Dans une ecole, on se cherche d'abord par formation : l'accueil montre
 * un bloc par formation, les visages de ses membres floutes en dessous de
 * son nom, et un toucher ouvre sa liste entiere. Une recherche, elle,
 * traverse tous les blocs. Sur un serveur sans formations, l'annuaire
 * reste une seule liste, par ordre alphabetique.
 */
export function AnnuaireEtudiantsScreen() {
  const { data, loading, error, reload, refresh, setData } = useAnnuaire();
  const [recherche, setRecherche] = useState('');
  const [toast, flash] = useToast();
  const [formationOuverte, setFormationOuverte] = useState(false);
  const [filtres, setFiltres] = useState(SANS_FILTRE);
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const boutonFiltres = useRef<HTMLButtonElement>(null);

  /*
    Une recherche ou un filtre traversent tous les blocs : on cherche
    alors des personnes, plus une formation.
  */
  const enRecherche = recherche.trim().length > 0 || filtreActif(filtres);
  const formations = data?.formations ?? [];
  const membres = data?.membres ?? [];
  const trouves = enRecherche ? filtrer(membres, recherche, filtres) : membres;
  const maFormation = formations.find((connue) => connue.id === data?.maFormation)?.nom;

  /* Dire sa formation, c'est rejoindre son bloc : la liste se relit derriere. */
  async function choisir(formation: string | null) {
    setFormationOuverte(false);
    try {
      await usersApi.enregistrerMaFormation(formation);
      setData((avant) => ({ ...avant, maFormation: formation }));
      flash(t.messages.annuaire.maFormation.enregistree);
      void refresh();
    } catch {
      flash(t.messages.annuaire.maFormation.impossible);
    }
  }

  return (
    <SubScreen
      title={t.messages.annuaire.accueil.etudiants}
      onRefresh={refresh}
      roue
      /* Par la fente du dessus, comme sur le fil : dans le contenu, le
         message s'ancrerait a la liste et tomberait n'importe ou. */
      overlay={
        <>
          {toast && <p className="feed__toast">{toast}</p>}
          <Popover
            open={filtresOuverts}
            anchor={boutonFiltres}
            onClose={() => setFiltresOuverts(false)}
            label={t.messages.annuaire.filtres.titre}
          >
            <FiltresDeLAnnuaire
              valeur={filtres}
              onChange={setFiltres}
              membres={membres}
              formations={formations}
              onFermer={() => setFiltresOuverts(false)}
            />
          </Popover>
          <Sheet
            open={formationOuverte}
            onClose={() => setFormationOuverte(false)}
            title={t.messages.annuaire.formation.aide}
          >
            {formations.map((formation) => (
              <SheetAction
                key={formation.id}
                icon={formation.id === data?.maFormation ? <Check size={20} weight="bold" /> : <GraduationCap size={20} />}
                onClick={() => void choisir(formation.id)}
              >
                {formation.nom}
              </SheetAction>
            ))}
            {data?.maFormation && (
              <SheetAction icon={<span />} onClick={() => void choisir(null)}>
                {t.messages.annuaire.maFormation.aucune}
              </SheetAction>
            )}
          </Sheet>
        </>
      }
    >
      <div className="an">
        {loading && !data && <ScreenLoader />}
        {error && <ScreenError message={error} onRetry={reload} />}

        {data && membres.length === 0 && formations.length === 0 && (
          <EmptyState
            icon={<AddressBook size={38} />}
            title={t.messages.annuaire.vide}
            hint={t.messages.annuaire.videAide}
          />
        )}

        {data && (membres.length > 0 || formations.length > 0) && (
          <>
            <div className="ev-search-row an__recherche">
              <SearchField
                tone="spot"
                className="ev-search-row__field search--croix-du-fil"
                placeholder={t.messages.annuaire.rechercher}
                value={recherche}
                onChange={(event) => setRecherche(event.target.value)}
                onClear={() => setRecherche('')}
              />
              {/* Un point sur l'entonnoir dit qu'un filtre retient la liste. */}
              <button
                ref={boutonFiltres}
                type="button"
                className="ev-filter-btn"
                aria-label={
                  filtreActif(filtres)
                    ? t.messages.annuaire.filtres.actifs
                    : t.messages.annuaire.filtres.filtrer
                }
                onClick={() => setFiltresOuverts(true)}
              >
                <FunnelSimple size={20} />
                {filtreActif(filtres) && <span className="ev-filter-btn__dot" aria-hidden="true" />}
              </button>
            </div>

            {/*
              Sa formation : la porte est la, a cote de ceux qu'on cherche.
              Tant qu'elle n'est pas dite, on n'apparait dans aucun bloc.
            */}
            {formations.length > 0 && (
              <button type="button" className="an__cv" onClick={() => setFormationOuverte(true)}>
                <GraduationCap size={18} />
                {maFormation
                  ? `${t.messages.annuaire.maFormation.bouton} · ${maFormation}`
                  : t.messages.annuaire.maFormation.choisir}
              </button>
            )}

            {enRecherche && trouves.length === 0 && (
              <p className="ms-notice ms-notice--recherche">
                {recherche.trim()
                  ? t.messages.annuaire.aucun(recherche.trim())
                  : t.messages.annuaire.filtres.aucun}
              </p>
            )}

            {enRecherche || formations.length === 0 ? (
              <ListeParLettre membres={trouves} onEchec={flash} />
            ) : (
              <Blocs formations={formations} membres={membres} />
            )}
          </>
        )}
      </div>
    </SubScreen>
  );
}

/**
 * Un bloc par formation, dans l'ordre de l'ecole, puis celui de ceux qui
 * n'ont rien dit — seulement s'il y en a.
 */
function Blocs({
  formations,
  membres,
}: {
  formations: FormationDuServeur[];
  membres: MembreDeLAnnuaire[];
}) {
  const sans = membres.filter((membre) => !membre.formation);
  return (
    <div className="an-blocs">
      {formations.map((formation) => (
        <Bloc
          key={formation.id}
          id={formation.id}
          nom={formation.nom}
          membres={membres.filter((membre) => membre.formation === formation.id)}
        />
      ))}
      {sans.length > 0 && (
        <Bloc id={SANS_FORMATION} nom={t.messages.annuaire.sansFormation} membres={sans} />
      )}
    </div>
  );
}

/** Combien de visages sous le nom : deux rangees de cinq. */
const VISAGES = 10;

/**
 * Le nom de la formation, et dessous les visages de ses membres, floutes.
 *
 * Le flou dit qu'il y a du monde sans encore le montrer : on reconnait
 * une foule, pas des personnes. C'est en ouvrant le bloc qu'on voit qui
 * est la. Une formation encore vide garde des places en attente, pour que
 * tous les blocs aient la meme allure.
 */
function Bloc({ id, nom, membres }: { id: string; nom: string; membres: MembreDeLAnnuaire[] }) {
  const visages = membres.slice(0, VISAGES);
  return (
    <Link
      to={`/app/annuaire/etudiants/${id}`}
      className="card an-bloc"
      aria-label={t.messages.annuaire.voir(nom)}
    >
      <span className="an-bloc__titre">{nom}</span>
      <span className="an-bloc__visages" aria-hidden="true">
        {visages.length > 0
          ? visages.map((membre) => (
              <Avatar key={membre.user.id} user={membre.user} size={40} />
            ))
          : Array.from({ length: 5 }, (_, rang) => <span key={rang} className="an-bloc__place" />)}
      </span>
    </Link>
  );
}
